// Jurisdiction resolver for the legal retrieval pipeline.
//
// Consumers supply every signal we can glean from the invoice + supporting
// documents; the resolver applies a strict priority order and reports back:
//
//   1. Service / tow / storage location
//   2. Vendor address
//   3. Incident location
//   4. ZIP code (any source above)
//   5. City + state (any source above)
//   6. Customer-entered claim metadata (final fallback only)
//
// The customer's home address is deliberately excluded unless it is also the
// service jurisdiction. When no signal reaches minimum confidence the
// resolver returns `state: null` and callers must surface
// "Jurisdiction could not be confirmed from the available documentation."
//
// This module is deterministic. It performs no network I/O.

export type JurisdictionSourceKind =
  | "service_location"
  | "vendor_address"
  | "incident_location"
  | "zip_code"
  | "city_state"
  | "claim_metadata"
  | "none";

export interface JurisdictionSignal {
  source: JurisdictionSourceKind;
  state?: string | null;
  city?: string | null;
  zip?: string | null;
  /** 0..1 — provider self-reported confidence. */
  confidence?: number;
  /** Free-form origin ("smarty", "ocr:service_address", "claim_form", …). */
  origin?: string;
}

export interface ResolvedJurisdiction {
  state: string | null;
  city: string | null;
  zip: string | null;
  /** 0..1 — computed confidence for the resolved answer. */
  confidence: number;
  /** Which of the signals won. */
  source: JurisdictionSourceKind;
  /** Human-readable origin for logs / debug panels. */
  origin?: string;
  /**
   * Other signals that named a DIFFERENT state. Non-empty means the caller
   * should surface a conflict warning to staff (never to the customer).
   */
  conflicts: Array<{ source: JurisdictionSourceKind; state: string; origin?: string }>;
  /** True when no signal cleared the minimum confidence threshold. */
  unresolved: boolean;
  /** Suggested customer-facing message when `unresolved` is true. */
  unresolvedMessage?: string;
}

/** Priority order — index 0 wins. */
const PRIORITY: JurisdictionSourceKind[] = [
  "service_location",
  "vendor_address",
  "incident_location",
  "zip_code",
  "city_state",
  "claim_metadata",
];

const UNRESOLVED_MESSAGE =
  "Jurisdiction could not be confirmed from the available documentation.";

const STATE_ABBR = new Set([
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME",
  "MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA",
  "RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR",
]);

function normalizeState(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = String(raw).trim().toUpperCase();
  if (trimmed.length === 2 && STATE_ABBR.has(trimmed)) return trimmed;
  const match = STATE_NAME_TO_ABBR.get(trimmed.toLowerCase());
  return match ?? null;
}

const STATE_NAME_TO_ABBR = new Map<string, string>([
  ["alabama","AL"],["alaska","AK"],["arizona","AZ"],["arkansas","AR"],["california","CA"],
  ["colorado","CO"],["connecticut","CT"],["delaware","DE"],["florida","FL"],["georgia","GA"],
  ["hawaii","HI"],["idaho","ID"],["illinois","IL"],["indiana","IN"],["iowa","IA"],
  ["kansas","KS"],["kentucky","KY"],["louisiana","LA"],["maine","ME"],["maryland","MD"],
  ["massachusetts","MA"],["michigan","MI"],["minnesota","MN"],["mississippi","MS"],
  ["missouri","MO"],["montana","MT"],["nebraska","NE"],["nevada","NV"],["new hampshire","NH"],
  ["new jersey","NJ"],["new mexico","NM"],["new york","NY"],["north carolina","NC"],
  ["north dakota","ND"],["ohio","OH"],["oklahoma","OK"],["oregon","OR"],["pennsylvania","PA"],
  ["rhode island","RI"],["south carolina","SC"],["south dakota","SD"],["tennessee","TN"],
  ["texas","TX"],["utah","UT"],["vermont","VT"],["virginia","VA"],["washington","WA"],
  ["west virginia","WV"],["wisconsin","WI"],["wyoming","WY"],["district of columbia","DC"],
  ["puerto rico","PR"],
]);

/**
 * Deterministic ZIP-prefix → state map (USPS 3-digit prefix ranges).
 * Used only when no explicit state signal exists.
 */
const ZIP_PREFIX_RANGES: Array<[number, number, string]> = [
  [5, 5, "NY"], [6, 9, "PR"], [10, 27, "MA"], [28, 29, "RI"], [30, 38, "NH"],
  [39, 49, "ME"], [50, 59, "VT"], [60, 69, "CT"], [70, 89, "NJ"], [100, 149, "NY"],
  [150, 196, "PA"], [197, 199, "DE"], [200, 200, "DC"], [201, 201, "VA"],
  [202, 205, "DC"], [206, 219, "MD"], [220, 246, "VA"], [247, 268, "WV"],
  [270, 289, "NC"], [290, 299, "SC"], [300, 319, "GA"], [320, 349, "FL"],
  [350, 369, "AL"], [370, 385, "TN"], [386, 397, "MS"], [398, 399, "GA"],
  [400, 427, "KY"], [430, 459, "OH"], [460, 479, "IN"], [480, 499, "MI"],
  [500, 528, "IA"], [530, 549, "WI"], [550, 567, "MN"], [569, 569, "DC"],
  [570, 577, "SD"], [580, 588, "ND"], [590, 599, "MT"], [600, 629, "IL"],
  [630, 658, "MO"], [660, 679, "KS"], [680, 693, "NE"], [700, 714, "LA"],
  [716, 729, "AR"], [730, 749, "OK"], [750, 799, "TX"], [800, 816, "CO"],
  [820, 831, "WY"], [832, 838, "ID"], [840, 847, "UT"], [850, 865, "AZ"],
  [870, 884, "NM"], [889, 898, "NV"], [900, 961, "CA"], [967, 968, "HI"],
  [970, 979, "OR"], [980, 994, "WA"], [995, 999, "AK"],
];

/** Resolve a two-letter state from a normalized 5-digit ZIP, or null. */
export function stateFromZip(zip: string | null | undefined): string | null {
  if (!zip || !/^\d{5}$/.test(zip)) return null;
  const prefix = Number(zip.slice(0, 3));
  for (const [lo, hi, st] of ZIP_PREFIX_RANGES) {
    if (prefix >= lo && prefix <= hi) return st;
  }
  return null;
}

function normalizeZip(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length < 5) return null;
  const z = digits.slice(0, 5);
  if (z === "00000") return null;
  return z;
}

function normalizeCity(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = String(raw).trim();
  return s ? s : null;
}

/**
 * Resolve jurisdiction from a bag of signals. `signals` may contain nulls,
 * duplicates, or partial data — the resolver picks the highest-priority
 * signal with a non-null state and reasonable confidence.
 */
export function resolveJurisdiction(signals: JurisdictionSignal[]): ResolvedJurisdiction {
  // Normalize + drop empty entries.
  const normalized = signals
    .map((s) => {
      const zip = normalizeZip(s.zip);
      // A five-digit US ZIP deterministically identifies a state. Without
      // this derivation a ZIP-only signal (very common when the invoice
      // omits a printed vendor address) left jurisdiction unresolved and
      // permanently stalled legal research.
      const state = normalizeState(s.state) ?? stateFromZip(zip);
      return {
        source: s.source,
        state,
        city: normalizeCity(s.city),
        zip,
        confidence:
          typeof s.confidence === "number" ? Math.max(0, Math.min(1, s.confidence)) : 0.5,
        origin: s.origin,
      };
    })
    .filter((s) => s.state || s.city || s.zip);

  if (normalized.length === 0) {
    return {
      state: null,
      city: null,
      zip: null,
      confidence: 0,
      source: "none",
      conflicts: [],
      unresolved: true,
      unresolvedMessage: UNRESOLVED_MESSAGE,
    };
  }

  // Highest-priority signal that names a state.
  let winner: (typeof normalized)[number] | null = null;
  for (const kind of PRIORITY) {
    const match = normalized.find((s) => s.source === kind && s.state);
    if (match) {
      winner = match;
      break;
    }
  }

  // If no signal has a state, try one that only has a ZIP or city — still
  // report unresolved for state but expose the city/zip we did find.
  if (!winner) {
    const partial = normalized.find((s) => s.zip || s.city) ?? null;
    return {
      state: null,
      city: partial?.city ?? null,
      zip: partial?.zip ?? null,
      confidence: 0.1,
      source: partial?.source ?? "none",
      origin: partial?.origin,
      conflicts: [],
      unresolved: true,
      unresolvedMessage: UNRESOLVED_MESSAGE,
    };
  }

  // Collect city/zip: prefer the winner's, then fill from other same-state
  // signals. Never inherit from a conflicting-state signal.
  let city = winner.city;
  let zip = winner.zip;
  for (const s of normalized) {
    if (s.state && s.state !== winner.state) continue;
    if (!city && s.city) city = s.city;
    if (!zip && s.zip) zip = s.zip;
  }

  const conflicts = normalized
    .filter((s) => s.state && s.state !== winner!.state)
    .map((s) => ({ source: s.source, state: s.state!, origin: s.origin }));

  // Confidence blends the winner's confidence with a small penalty per
  // conflict (never below 0.2 when we do have a state).
  const conflictPenalty = Math.min(0.4, conflicts.length * 0.15);
  const priorityBoost = 1 - PRIORITY.indexOf(winner.source) * 0.05;
  const confidence = Math.max(0.2, Math.min(1, winner.confidence * priorityBoost - conflictPenalty));

  return {
    state: winner.state,
    city,
    zip,
    confidence,
    source: winner.source,
    origin: winner.origin,
    conflicts,
    unresolved: false,
  };
}

/** Convenience: normalize a bag of common signals from a normalized invoice. */
export interface InvoiceSignalBag {
  serviceLocation?: { state?: string | null; city?: string | null; zip?: string | null; confidence?: number };
  vendorAddress?: { state?: string | null; city?: string | null; zip?: string | null };
  incidentLocation?: { state?: string | null; city?: string | null; zip?: string | null };
  zipCode?: string | null;
  cityState?: { city?: string | null; state?: string | null };
  claimMetadata?: { state?: string | null; city?: string | null; zip?: string | null };
}

export function resolveJurisdictionFromInvoice(bag: InvoiceSignalBag): ResolvedJurisdiction {
  const signals: JurisdictionSignal[] = [];
  if (bag.serviceLocation) {
    signals.push({ source: "service_location", ...bag.serviceLocation, origin: "service_location" });
  }
  if (bag.vendorAddress) {
    signals.push({ source: "vendor_address", ...bag.vendorAddress, origin: "vendor_address" });
  }
  if (bag.incidentLocation) {
    signals.push({ source: "incident_location", ...bag.incidentLocation, origin: "incident_location" });
  }
  if (bag.zipCode) {
    signals.push({ source: "zip_code", zip: bag.zipCode, origin: "invoice_zip" });
  }
  if (bag.cityState) {
    signals.push({ source: "city_state", ...bag.cityState, origin: "invoice_city_state" });
  }
  if (bag.claimMetadata) {
    signals.push({ source: "claim_metadata", ...bag.claimMetadata, origin: "claim_form" });
  }
  return resolveJurisdiction(signals);
}
